// src/components/planner/modals/SubjectModal.tsx

import React, { useState } from 'react';
import { X, Save } from 'lucide-react';

const API_BASE_URL = '/api';

interface SubjectModalProps {
    onClose: () => void;
    onSave: () => void;
    onShowToast: (message: string, type: 'success' | 'error' | 'info') => void;
}

const SubjectModal: React.FC<SubjectModalProps> = ({ onClose, onSave, onShowToast }) => {
    const [formData, setFormData] = useState({
        name: '',
        priority: 'medium',
        status: 'not-started',
        targetMinutes: '',
        weight: 5,
        deadline: '',
        notes: ''
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.name.trim()) {
            onShowToast('Preencha o nome do assunto', 'error');
            return;
        }

        try {
            const token = localStorage.getItem('accessToken');
            const payload = {
                ...formData,
                targetMinutes: formData.targetMinutes ? parseInt(formData.targetMinutes) : undefined,
                deadline: formData.deadline || undefined
            };

            const response = await fetch(`${API_BASE_URL}/planner/subjects`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                onShowToast('Assunto criado!', 'success');
                onSave();
            } else {
                onShowToast('Erro ao criar assunto', 'error');
            }
        } catch (error) {
            onShowToast('Erro ao criar assunto', 'error');
        }
    };

    return (
        <div className="plnr-modal-overlay" onClick={onClose}>
            <div className="plnr-modal plnr-modal-large" onClick={(e) => e.stopPropagation()}>
                <div className="plnr-modal-header">
                    <h3>Novo Assunto</h3>
                    <button className="plnr-modal-close" onClick={onClose}>
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="plnr-modal-body">
                        <div className="plnr-form-group">
                            <label>Nome do assunto *</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Ex: Cardiologia"
                                className="plnr-input"
                                required
                            />
                        </div>

                        <div className="plnr-form-row">
                            <div className="plnr-form-group">
                                <label>Prioridade</label>
                                <select
                                    value={formData.priority}
                                    onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                    className="plnr-input"
                                >
                                    <option value="high">Alta</option>
                                    <option value="medium">Média</option>
                                    <option value="low">Baixa</option>
                                </select>
                            </div>

                            <div className="plnr-form-group">
                                <label>Status</label>
                                <select
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    className="plnr-input"
                                >
                                    <option value="not-started">Não iniciado</option>
                                    <option value="in-progress">Em andamento</option>
                                    <option value="completed">Concluído</option>
                                    <option value="reviewing">Em revisão</option>
                                </select>
                            </div>
                        </div>

                        <div className="plnr-form-row">
                            <div className="plnr-form-group">
                                <label>Meta de tempo (minutos)</label>
                                <input
                                    type="number"
                                    value={formData.targetMinutes}
                                    onChange={(e) => setFormData({ ...formData, targetMinutes: e.target.value })}
                                    placeholder="Ex: 300"
                                    className="plnr-input"
                                    min="0"
                                />
                            </div>

                            <div className="plnr-form-group">
                                <label>Peso (1-10)</label>
                                <input
                                    type="number"
                                    value={formData.weight}
                                    onChange={(e) => setFormData({ ...formData, weight: parseInt(e.target.value) })}
                                    className="plnr-input"
                                    min="1"
                                    max="10"
                                />
                            </div>
                        </div>

                        <div className="plnr-form-group">
                            <label>Prazo (opcional)</label>
                            <input
                                type="date"
                                value={formData.deadline}
                                onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                                className="plnr-input"
                            />
                        </div>

                        <div className="plnr-form-group">
                            <label>Notas (opcional)</label>
                            <textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                placeholder="Observações sobre este assunto..."
                                className="plnr-textarea"
                                rows={3}
                            />
                        </div>
                    </div>

                    <div className="plnr-modal-footer">
                        <button type="button" className="plnr-btn secondary" onClick={onClose}>
                            Cancelar
                        </button>
                        <button type="submit" className="plnr-btn primary">
                            <Save size={16} />
                            Salvar Assunto
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default SubjectModal;